import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { dashboardService } from '../../../services/dashboardService';
import { inventoryService } from '../../../services/inventoryService';
import { greenBeanService } from '../../../services/greenBeanService';
import { salesOrderService } from '../../../services/salesOrderService';
import type { DashboardStats, PurchaseOrderExpenseOverview } from '../../../services/dashboardService';
import type { RoastedBulkGroup } from '../../../types/inventory';
import { usePermissions } from '../../../hooks/usePermissions';
import { isForbiddenError } from '../../../services/api';

interface UseDashboardDataProps {
  startDate: string;
  endDate: string;
  greenBeanPage: string | number;
  isExpensesModalOpen: boolean;
}

export const useDashboardData = ({
  startDate,
  endDate,
  greenBeanPage,
  isExpensesModalOpen,
}: UseDashboardDataProps) => {
  const { can } = usePermissions();
  // Stats endpoint returns all financial + KPI fields; enabled if user has any
  // financial dashboard widget read OR the average shrinkage read (spec 101).
  const canReadFinancials =
    can('dashboard.avg_shrinkage', 'read') ||
    can('dashboard.outstanding_payment', 'read') ||
    can('dashboard.revenue', 'read') ||
    can('dashboard.expenses', 'read') ||
    can('dashboard.net_profit', 'read') ||
    can('dashboard.green_bean_value', 'read') ||
    can('dashboard.roasted_value', 'read') ||
    can('dashboard.finished_value', 'read');
  const canReadSalesOrders = can('sales_orders', 'read');
  const canReadInventory = can('inventory', 'read');
  const canReadGreenBeanProfiles = can('green_bean_profiles', 'read');
  const canReadPurchaseOrders = can('purchase_orders', 'read');

  const isDateWithinRange = (date: string, start: string, end: string) => {
    const normalizedDate = date?.slice(0, 10);
    const normalizedStart = start?.slice(0, 10);
    const normalizedEnd = end?.slice(0, 10);
    if (!normalizedDate || !normalizedStart || !normalizedEnd) return false;
    return normalizedDate >= normalizedStart && normalizedDate <= normalizedEnd;
  };

  const {
    data: statsData,
    isLoading: statsLoading,
    isFetching: statsFetching,
    isError: statsIsError,
    error: statsQueryError,
  } = useQuery<DashboardStats>({
    queryKey: ['dashboard-stats', startDate, endDate],
    queryFn: () => dashboardService.getStats(startDate, endDate),
    placeholderData: keepPreviousData,
    enabled: canReadFinancials,
    retry: (failureCount, err) => !isForbiddenError(err) && failureCount < 3,
  });

  const { data: salesOrdersData } = useQuery({
    queryKey: ['sales-orders-dashboard-revenue'],
    queryFn: () => salesOrderService.getAll(),
    staleTime: 60 * 1000,
    placeholderData: keepPreviousData,
    enabled: canReadSalesOrders,
    retry: (failureCount, err) => !isForbiddenError(err) && failureCount < 3,
  });

  const deliveredRevenue = (salesOrdersData ?? []).reduce((sum, order) => {
    const isDelivered = order.status === 'Delivered';
    const isInRange = isDateWithinRange(order.orderDate, startDate, endDate);
    const totalAmount = typeof order.totalAmount === 'number' && Number.isFinite(order.totalAmount) ? order.totalAmount : 0;

    if (!isDelivered || !isInRange) return sum;
    return sum + totalAmount;
  }, 0);

  const stats = statsData
    ? {
        ...statsData,
        total_sales: salesOrdersData ? deliveredRevenue : statsData.total_sales,
      }
    : null;
  const statsForbidden = !canReadFinancials || isForbiddenError(statsQueryError);
  const statsError = statsIsError && !statsForbidden
    ? 'Failed to load dashboard statistics. Please try again later.'
    : null;

  const {
    data: roastedGroupsData,
    isLoading: roastedGroupsLoading,
    isError: roastedGroupsIsError,
    error: roastedGroupsQueryError,
  } = useQuery<RoastedBulkGroup[]>({
    queryKey: ['roasted-bulk-groups'],
    queryFn: () => inventoryService.getRoastedBulkGroups(false),
    staleTime: 60 * 1000,
    enabled: canReadInventory,
    retry: (failureCount, err) => !isForbiddenError(err) && failureCount < 3,
  });
  const roastedGroups = roastedGroupsData ?? [];
  const roastedGroupsForbidden = !canReadInventory || isForbiddenError(roastedGroupsQueryError);

  const {
    data: greenBeanProfilesData,
    isLoading: greenBeanProfilesLoading,
    isError: greenBeanProfilesIsError,
    error: greenBeanProfilesQueryError,
  } = useQuery({
    queryKey: ['green-bean-profiles-distribution'],
    queryFn: greenBeanService.getAll,
    staleTime: 5 * 60 * 1000,
    enabled: canReadGreenBeanProfiles,
    retry: (failureCount, err) => !isForbiddenError(err) && failureCount < 3,
  });
  const greenBeanProfiles = greenBeanProfilesData ?? [];
  const greenBeanProfilesForbidden = !canReadGreenBeanProfiles || isForbiddenError(greenBeanProfilesQueryError);

  const totalRoastedKg = roastedGroups.reduce((sum, g) => {
    const v = typeof g.totalStock === 'number' && Number.isFinite(g.totalStock) ? g.totalStock : 0;
    return sum + v;
  }, 0);

  const roastedGroupsSorted = [...roastedGroups].sort((a, b) => {
    const av = typeof a.totalStock === 'number' && Number.isFinite(a.totalStock) ? a.totalStock : 0;
    const bv = typeof b.totalStock === 'number' && Number.isFinite(b.totalStock) ? b.totalStock : 0;
    return bv - av;
  });

  const greenBeanLotsLimit = 10;
  const greenBeanLotsOffset = (Number(greenBeanPage) - 1) * greenBeanLotsLimit;

  const {
    data: greenBeanLotsData,
    isLoading: greenBeanLotsLoading,
    isError: greenBeanLotsIsError,
    error: greenBeanLotsQueryError,
  } = useQuery({
    queryKey: ['green-bean-lots-dashboard', startDate, endDate, greenBeanPage],
    queryFn: () =>
      inventoryService.getAllLots({
        item_type: 'green_bean',
        status: 'active',
        start_date: startDate,
        end_date: endDate,
        order_by: 'remaining_quantity',
        order_dir: 'desc',
        limit: greenBeanLotsLimit,
        offset: greenBeanLotsOffset,
      }),
    placeholderData: keepPreviousData,
    enabled: canReadInventory,
    retry: (failureCount, err) => !isForbiddenError(err) && failureCount < 3,
  });
  const greenBeanLotsForbidden = !canReadInventory || isForbiddenError(greenBeanLotsQueryError);

  const greenBeanLots = greenBeanLotsData?.data ?? [];
  const greenBeanLotsTotal = greenBeanLotsData?.total ?? 0;
  const greenBeanTotalKg = greenBeanLotsData?.totalRemainingQuantity;
  const greenBeanTotalPages = Math.max(1, Math.ceil(greenBeanLotsTotal / greenBeanLotsLimit));

  const {
    data: poExpenseOverviewData,
    isLoading: poExpenseOverviewLoading,
    isError: poExpenseOverviewIsError,
  } = useQuery<PurchaseOrderExpenseOverview[]>({
    queryKey: ['dashboard-po-expenses', startDate, endDate],
    queryFn: () => dashboardService.getPurchaseOrderExpenses(startDate, endDate),
    enabled: isExpensesModalOpen && canReadPurchaseOrders,
    placeholderData: keepPreviousData,
    retry: (failureCount, err) => !isForbiddenError(err) && failureCount < 3,
  });
  const poExpenseOverview = poExpenseOverviewData ?? [];

  return {
    stats,
    statsLoading,
    statsFetching,
    statsError,
    statsForbidden,
    canReadFinancials,
    roastedGroupsSorted,
    roastedGroupsLoading,
    roastedGroupsIsError,
    roastedGroupsForbidden,
    totalRoastedKg,
    greenBeanProfiles,
    greenBeanProfilesLoading,
    greenBeanProfilesIsError,
    greenBeanProfilesForbidden,
    greenBeanLots,
    greenBeanLotsLoading,
    greenBeanLotsIsError,
    greenBeanLotsForbidden,
    greenBeanTotalKg,
    greenBeanTotalPages,
    poExpenseOverview,
    poExpenseOverviewLoading,
    poExpenseOverviewIsError,
  };
};
